import { useState } from 'react';
import { View, type LayoutChangeEvent } from 'react-native';
import Svg, { Rect } from 'react-native-svg';
import { format } from 'date-fns';
import { Text } from '@/components/ui/text';
import { useColor } from '~/global/hooks/useColor';
import type { TrendPoint } from '~/health/lib/body-measurement/body-trend-series';

interface BodyTrendChartProps {
  points: TrendPoint[];
  height?: number;
}

const BAR_GAP = 6;
const MIN_BAR = 8;

/**
 * A single `react-native-svg` bar-trend chart — renders on native and web (via
 * react-native-web), so the ticket's "on web nothing breaks" holds without a
 * platform split or a native charting dependency. Bars are scaled between the
 * series min/max so a narrow-band metric (like weight) still reads as a trend;
 * the latest weigh-in is highlighted in the accent, the rest muted. Colors come
 * from resolved tokens (SVG fills can't take Tailwind classes).
 */
export const BodyTrendChart = ({
  points,
  height = 200,
}: BodyTrendChartProps) => {
  const [width, setWidth] = useState(0);
  const accent = useColor('accent');
  const muted = useColor('border');
  // Ignore sub-pixel width changes. On web `onLayout` (ResizeObserver) can re-fire
  // with fractionally-different widths every render; setting state from each one
  // loops forever (re-render → re-measure → re-render …) and freezes the JS thread.
  const onLayout = (e: LayoutChangeEvent) => {
    const next = e.nativeEvent.layout.width;
    setWidth((prev) => (Math.abs(prev - next) < 1 ? prev : next));
  };

  const values = points.map((p) => p.value);
  const min = values.length ? Math.min(...values) : 0;
  const max = values.length ? Math.max(...values) : 0;
  const span = max - min || 1;
  const count = points.length;
  const barWidth =
    count > 0 && width > 0
      ? Math.max((width - BAR_GAP * (count - 1)) / count, 1)
      : 0;

  const axisLabels =
    count > 0
      ? [points[0], points[Math.floor((count - 1) / 2)], points[count - 1]]
      : [];

  return (
    <View className="gap-2" testID="body-trend-chart">
      <View onLayout={onLayout} style={{ height }}>
        {width > 0 && count > 0 && (
          <Svg width={width} height={height}>
            {points.map((point, index) => {
              const fraction = (point.value - min) / span;
              const barHeight = MIN_BAR + fraction * (height - MIN_BAR);
              const x = index * (barWidth + BAR_GAP);
              const isLatest = index === count - 1;
              return (
                <Rect
                  key={`${point.date.getTime()}-${index}`}
                  x={x}
                  y={height - barHeight}
                  width={barWidth}
                  height={barHeight}
                  rx={4}
                  fill={isLatest ? accent : muted}
                />
              );
            })}
          </Svg>
        )}
      </View>
      {axisLabels.length > 0 && (
        <View className="flex-row justify-between">
          {axisLabels.map((point, index) => (
            <Text key={`axis-${index}`} className="text-xs text-text-muted">
              {format(point.date, 'MMM d')}
            </Text>
          ))}
        </View>
      )}
    </View>
  );
};
